import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";

export default function ChangePasswordModal({ open, onClose }) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    setShowCurrent(false); setShowNew(false);
  };

  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("New password and confirmation do not match"); return; }
    if (newPwd === currentPwd) { toast.error("New password must be different from current password"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
      toast.success("Password changed successfully");
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[440px]" data-testid="change-password-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#29ABE2]/10 text-[#187199] flex items-center justify-center">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>Use at least 6 characters. You&apos;ll stay signed in after the change.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="current-pwd">Current Password</Label>
            <div className="relative">
              <Input
                id="current-pwd"
                data-testid="current-password-input"
                type={showCurrent ? "text" : "password"}
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                autoComplete="current-password"
                required
                className="pr-10"
              />
              <button type="button" onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                tabIndex={-1} aria-label="Toggle current password visibility">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pwd">New Password</Label>
            <div className="relative">
              <Input
                id="new-pwd"
                data-testid="new-password-input"
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
                className="pr-10"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                tabIndex={-1} aria-label="Toggle new password visibility">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pwd">Confirm New Password</Label>
            <Input
              id="confirm-pwd"
              data-testid="confirm-password-input"
              type={showNew ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
            />
            {confirmPwd && newPwd !== confirmPwd && (
              <p className="text-xs text-red-600">Passwords do not match</p>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting} data-testid="change-password-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="change-password-submit-btn"
              className="bg-[#1B4F8B] hover:bg-[#163e6e] text-white">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>) : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
